import { LevelTypes } from './AllEventParams';
import { IBoardData } from '../Game/BoardUtils';
import { IGoalData } from '../Config/Levels';
import BoardUtils from '../Game/BoardUtils';
import { Suits } from '../Tiles/TileSet';
import Core from '../Core/Core';
import { EventBuilder } from './EventBuilder';
import { DDNAEvents } from './Tracker';

export interface IMissionParamsAll {
    mLevel: number;
    mLevelID: string; // this is listed as number but would be a string in MOJI's case,
    mLevelDifficulty: number; // we don't have difficulty, but we may in the future
    mLevelHard: number; // 1 if hard, 0 if not
    mLevelType: LevelTypes; // goal type
    mGoalAmount: number; // goal target
    mRetryNum: number;
    mIsSuccessRetry: number; // 0/1 boolean if has completed level (state stars === 0)
}

const defaultMissionAll: IMissionParamsAll = {
    mLevel: 0,
    mLevelID: 'machu-1',
    mLevelDifficulty: 0,
    mLevelHard: 0,
    mLevelType: LevelTypes.ALL_TILES,
    mGoalAmount: 0,
    mRetryNum: 0,
    mIsSuccessRetry: 0,
};

export interface IMissionParamsEndTracked {
    mMagnifyingGlassUsed: number;
    mFlashLightUsed: number;
    mFireCrackerUsed: number;
    mMagnetUsed: number;
    mShuffleUsed: number;
    mCoinsSpent: number;
    mCoinsBoughtWithAds: number;
    mGoalsCompletePercentage: number;
    mPossibleMovesLeft: number;
    mLossAversionCoinsSpent: number;
}

export interface IMissionParamsEnd extends IMissionParamsEndTracked {
    mStars: number; //.
    mScore: number; //.
}

const defaultMissionEnd: IMissionParamsEnd = {
    mMagnifyingGlassUsed: 0,
    mFlashLightUsed: 0,
    mFireCrackerUsed: 0,
    mMagnetUsed: 0,
    mShuffleUsed: 0,
    mCoinsSpent: 0,
    mCoinsBoughtWithAds: 0,
    mGoalsCompletePercentage: 0,
    mPossibleMovesLeft: 0,
    mStars: 0,
    mLossAversionCoinsSpent: 0,
    mScore: 0,
};

export interface IMissionTrackingData {
    mLevel: number;
    mLevelID: string;
    mLevelDifficulty: 0;
    mLevelHard: 0;
    mLevelType: LevelTypes;
    mGoalAmount: number;
    mRetryNum: number;
    mIsSuccessRetry: number;
    mMagnifyingGlassUsed: number;
    mFlashLightUsed: number;
    mFireCrackerUsed: number;
    mMagnetUsed: number;
    mShuffleUsed: number;
    mCoinsSpent: number;
    mCoinsBoughtWithAds: number;
    mGoalsCompletePercentage: number;
    mPossibleMovesLeft: number;
    mLossAversionCoinsSpent: number;
}

const defaultMissionTrackData: IMissionTrackingData = {
    mLevel: 0,
    mLevelID: 'machu-1',
    mLevelDifficulty: 0,
    mLevelHard: 0,
    mLevelType: LevelTypes.ALL_TILES,
    mGoalAmount: 0,
    mRetryNum: 0,
    mIsSuccessRetry: 0,
    mMagnifyingGlassUsed: 0,
    mFlashLightUsed: 0,
    mFireCrackerUsed: 0,
    mMagnetUsed: 0,
    mShuffleUsed: 0,
    mCoinsSpent: 0,
    mCoinsBoughtWithAds: 0,
    mGoalsCompletePercentage: 0,
    mPossibleMovesLeft: 0,
    mLossAversionCoinsSpent: 0,
    // mAdsWatchedDoubleReward: 0,
};

export interface IMissionStartValues {
    index: number;
    id: string;
    data: IBoardData;
    wasCompleted: boolean;
    retries: number;
}

export class MissionParamBuilder extends EventBuilder {
    private curMissionData: IMissionTrackingData;

    constructor() {
        super();

        this.curMissionData = { ...defaultMissionTrackData };
    }

    public setParamsFromGame(startData: IMissionStartValues): void {
        this.curMissionData = {
            mLevel: startData.index,
            mLevelID: startData.id,
            mLevelDifficulty: 0,
            mLevelHard: 0,
            mLevelType: this.getLevelType(startData.data.config.goal),
            mGoalAmount: this.getLevelGoal(startData.data),
            mRetryNum: startData.retries,
            mIsSuccessRetry: startData.wasCompleted ? 1 : 0,
            mMagnifyingGlassUsed: 0,
            mFlashLightUsed: 0,
            mFireCrackerUsed: 0,
            mMagnetUsed: 0,
            mShuffleUsed: 0,
            mCoinsSpent: 0,
            mCoinsBoughtWithAds: 0,
            mGoalsCompletePercentage: 0,
            mPossibleMovesLeft: 0,
            mLossAversionCoinsSpent: 0,
        };
    }

    public getCurrentMissionData() {
        return this.curMissionData;
    }

    public setParams(keys: Map<string, any>): void {
        keys.forEach((_, key) => {
            if (this.curMissionData.hasOwnProperty(key)) {
                const c: any = this.curMissionData;
                c[key] = keys.get(key);
            }
        });
    }

    public getParams<T>(key: string): IMissionParamsAll | IMissionParamsEnd {
        if (key === DDNAEvents.MISSION_STARTED) {
            return this.getMissionAllParams();
        }

        if (key === DDNAEvents.MISSION_COMPLETED || key === DDNAEvents.MISSION_FAILED) {
            return {
                ...this.getMissionAllParams(),
                ...this.getMissionEndParams(),
            };
        }
        return this.getMissionAllParams();
    }

    public getMissionAllParams(): IMissionParamsAll {
        const currentValObj: IMissionTrackingData = { ...this.getCurrentMissionData() };
        return Object.keys(defaultMissionAll).reduce(
            (r: any, key) => {
                const n: any = currentValObj;
                r[key] = n[key];
                return r;
            },
            {} as IMissionParamsAll
        );
    }

    public getMissionEndParams(): IMissionParamsEnd {
        const currentValObj: IMissionTrackingData = { ...this.getCurrentMissionData() };
        const trackedEndValues: IMissionParamsEndTracked = Object.keys(defaultMissionEnd).reduce(
            (r: any, key) => {
                const c: any = currentValObj;
                r[key] = c[key];
                return r;
            },
            {} as IMissionParamsEndTracked
        );

        const state = Core.store.getState();
        const stateVals = {
            mScore: state.StateScore.score,
            mStars: state.StatePlayer.mapLevels[this.curMissionData.mLevelID]
                ? state.StatePlayer.mapLevels[this.curMissionData.mLevelID].stars
                : 0,
        };

        return {
            ...trackedEndValues,
            ...stateVals,
        };
    }

    private getLevelType(data: IGoalData): LevelTypes {
        if (data.type === 'toys') {
            return LevelTypes.TOYS;
        }

        if (data.type === 'gold') {
            return LevelTypes.GOLDEN_TILES;
        }

        return LevelTypes.ALL_TILES;
    }

    private getLevelGoal(data: IBoardData): number {
        if (data.config.goal.target === 'all') {
            const nonBoosterTiles = data.tiles.filter(
                tile => BoardUtils.convertTileData(tile.type).suit === Suits.BOOSTER
            );
            return nonBoosterTiles.length;
        }

        if (typeof data.config.goal.target === 'number') {
            return data.config.goal.target;
        }

        return 0;
    }
}
